'use client';

import { FileWarning } from 'lucide-react';

import { StatusBadge } from '@/components/ui/status-badge';
import { cn } from '@/lib/utils';
import type { AnalysisFailureReasonCode } from '@/lib/services/files/types';
import { getUnreadableDocumentCopy } from '@/lib/services/files/unreadable-document-copy';

interface UnreadableDocumentNoticeProps {
  /** Omit (or pass undefined) for a document that was read — the notice renders nothing. */
  reasonCode?: AnalysisFailureReasonCode;
  /**
   * `badge` renders the chip alone, for a dense row that has no space for a sentence.
   * `full` adds the explanatory line beneath it.
   */
  variant?: 'badge' | 'full';
  className?: string;
}

/**
 * Flags a document whose contents Vale could not read, so the firm can tell it apart
 * from one that was analysed successfully and genuinely had nothing to extract.
 *
 * Renders `null` for a readable document, so a call site can drop it in unconditionally
 * beside the certified-copy badge without a surrounding guard — the same shape those
 * badges already use.
 *
 * The explanatory sentence is rendered INLINE rather than in a tooltip. A tooltip inside
 * these cards is a known hazard (a non-portalled one is clipped invisible by an
 * `overflow-hidden` ancestor while still measuring full size in the DOM, which is how
 * VALE-347 shipped), and this state is rare enough that the extra line costs nothing on
 * the overwhelmingly common healthy card while being impossible to miss on the broken
 * one. It is also the sentence that tells the lawyer what to do about it, which is not
 * something to hide behind a hover.
 */
export function UnreadableDocumentNotice({
  reasonCode,
  variant = 'full',
  className
}: UnreadableDocumentNoticeProps) {
  if (!reasonCode) return null;

  const { label, detail } = getUnreadableDocumentCopy(reasonCode);

  if (variant === 'badge') {
    return (
      <StatusBadge
        tone="caution"
        icon={FileWarning}
        className={cn('shrink-0', className)}
        // The badge-only variant has no room for the sentence, so both the
        // accessible name and a native tooltip carry it — otherwise "Couldn't be
        // read" is all anyone gets, with no cause and no remedy. `title` rather
        // than a Tooltip component deliberately: every surface that needs this
        // variant needs it *because* it is a fixed-height `overflow-hidden` card,
        // which is the exact ancestor that renders a non-portalled tooltip
        // invisible while it still measures full size in the DOM (VALE-347).
        title={detail}
        aria-label={detail}
      >
        {label}
      </StatusBadge>
    );
  }

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <StatusBadge tone="caution" icon={FileWarning} className="self-start">
        {label}
      </StatusBadge>
      <p className="text-muted-foreground text-xs">{detail}</p>
    </div>
  );
}
